import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormContext } from "react-hook-form";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import {
    FORM_KEYS,
    multiple_users_helpers_keys,
} from "repoV2/features/Common/modules/FormData/FormData.constants";
import { IAppliedCouponCodeDetails } from "repoV2/features/Common/modules/FormData/interfaces/IAppliedCouponCodeDetails";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import { single_value_count } from "repoV2/features/CheckoutPages/constants/CheckoutPages.constants";
import { IPriceDetailsSection } from "repoV2/features/CheckoutPages/interfaces/CheckoutPages.interface";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { DISCOUNT_OFF_TYPES_IN_APPLY_CODE_ON_LISTING_API } from "repoV2/features/Payments/modules/CouponCode/CouponCode.constants";
import { IMultipleUsersHelpers } from "repoV2/features/Common/modules/FormData/interfaces/MultipleUserHelpers.interfaces";
import { CHECKOUT_PAGES_ACTION_TYPES } from "repoV2/features/CheckoutPages/constants/CheckoutPagesReducer";
import { checkIsObjectEmpty } from "repoV2/utils/common/dataTypes/object";
import { callApplyCouponCodeOnListing } from "repoV2/features/Payments/modules/CouponCode/CouponCode.utils";
import {
    applied_coupon_form_key,
    coupon_code_input_field_name,
    default_applied_coupon_code_details,
    helper_text_types,
    loyalty_email,
    DISCOUNT_CODE_TYPES,
    COUPON_TYPES,
} from "./CouponCodeFormGroup.constants";
import { order_bumps_form_key } from "../OrderBumpsFormGroup/OrderBumpsFormGroup.constants";

export const useRemoveCouponCode = () => {
    const { listingUuid } = useCheckoutBasicDetailsContext();
    const { setValue } = useFormContext();
    const { setFormData } = useSetFormData();

    const dispatch = useDispatch();

    const clearAppliedCouponCode = () => {
        setFormData(
            `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
            default_applied_coupon_code_details
        );
        setValue(applied_coupon_form_key, default_applied_coupon_code_details);
    };

    const onRemoveCouponCodeClick = () => {
        dispatch({
            type: CHECKOUT_PAGES_ACTION_TYPES.SET_COUPON_CODE_HELPER_DATA,
            payload: {
                listingUuid,
                data: {
                    helperTextDetails: { type: helper_text_types.empty },
                },
            },
        });
        clearAppliedCouponCode();
        setValue(coupon_code_input_field_name, "");
    };

    return {
        clearAppliedCouponCode,
        onRemoveCouponCodeClick,
    };
};

export const deafult_applied_coupon_code_details = { applied: false };

export const useCouponCodeFormGroup = () => {
    const { notify, toast_type_keys } = useToastMessage();
    const { getValues, setValue } = useFormContext();
    const { setFormData } = useSetFormData();
    const dispatch = useDispatch();

    const { listingUuid } = useCheckoutBasicDetailsContext();
    const priceDetailsSection: IPriceDetailsSection = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.priceSection
    );
    const priceDetails = priceDetailsSection?.data;
    const { currency = "", currency_name: currencyName = "" } =
        priceDetails || {};

    const appliedCouponCodeData =
        useGetFormData<IAppliedCouponCodeDetails>({
            key: `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
        }) || default_applied_coupon_code_details;

    const allUserIds = useGetFormData<IMultipleUsersHelpers["allUserIds"]>({
        key: `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.all_user_ids}`,
    });

    const isMultipleUsersExist = allUserIds.length > single_value_count;

    // after we hit apply coupon code button
    // we need to disable button click till we get an api response
    // `processing` state is used to manage that
    const { isProcessing: processing = false } =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.couponCodeProcessing?.data
        ) || {};

    const {
        helperTextDetails = {
            type: helper_text_types.empty,
        },
    } =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.couponCodeHelperText?.data
        ) || {};

    const { clearAppliedCouponCode, onRemoveCouponCodeClick } =
        useRemoveCouponCode();

    const setCouponCodeProcessing = (processingValue: boolean) => {
        dispatch({
            type: CHECKOUT_PAGES_ACTION_TYPES.SET_COUPON_CODE_PROCESSING,
            payload: {
                listingUuid,
                data: {
                    isProcessing: processingValue,
                },
            },
        });
    };

    const setHelperTextOnSuccess = () => {
        dispatch({
            type: CHECKOUT_PAGES_ACTION_TYPES.SET_COUPON_CODE_HELPER_DATA,
            payload: {
                listingUuid,
                data: {
                    helperTextDetails: {
                        type: helper_text_types.success,
                        text: "Coupon applied!",
                    },
                },
            },
        });
    };

    useEffect(() => {
        if (appliedCouponCodeData.applied) {
            setHelperTextOnSuccess();
            setValue(
                coupon_code_input_field_name,
                appliedCouponCodeData.couponCode
            );
        }
    }, []);

    const onCouponCodeFieldChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (
            !e?.target?.value?.trim() &&
            helperTextDetails.type !== helper_text_types.empty
        ) {
            dispatch({
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_COUPON_CODE_HELPER_DATA,
                payload: {
                    listingUuid,
                    data: {
                        helperTextDetails: { type: helper_text_types.empty },
                    },
                },
            });
            clearAppliedCouponCode();
        }
    };

    const onApplyCouponCodeClick = ({
        code,
        couponType,
        onError,
    }: {
        code?: string;
        couponType?: number;
        onError?: () => void;
    }) => {
        if (processing) return;
        setCouponCodeProcessing(true);

        const data = getValues();
        const value = (code || data[coupon_code_input_field_name] || "")
            .trim()
            .toUpperCase();
        const customerEmail = data[loyalty_email];

        if (!value?.trim()) {
            const message = "Enter a Coupon Code";

            dispatch({
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_COUPON_CODE_HELPER_DATA,
                payload: {
                    listingUuid,
                    data: {
                        helperTextDetails: {
                            type: helper_text_types.error,
                            text: message,
                        },
                    },
                },
            });
            clearAppliedCouponCode();
            setCouponCodeProcessing(false);
            return;
        }

        const selectedOrderBumps = getValues(order_bumps_form_key) || {};

        callApplyCouponCodeOnListing({
            code: value,
            listingUuid,
            currencyName,
            appliedCount: 0,
            customerEmail,
            discountCodeType:
                couponType === COUPON_TYPES.LOYALTY_COUPON
                    ? DISCOUNT_CODE_TYPES.LOYALTY
                    : DISCOUNT_CODE_TYPES.NORMAL,
            isUpsellOrder: !checkIsObjectEmpty(selectedOrderBumps),
        })
            .then((res: any) => {
                const response = res?.data?.data?.discount;
                let couponCodeData: IAppliedCouponCodeDetails;
                if (response) {
                    couponCodeData = {
                        applied: true,
                        couponCode: value,
                        includesGst: response.include_gst,
                        usageCount: response.usage_count,
                        usageLimit: response.usage_limit,
                        value: response.value,
                        couponType,
                        discountOffType:
                            DISCOUNT_OFF_TYPES_IN_APPLY_CODE_ON_LISTING_API[
                                response.type as keyof typeof DISCOUNT_OFF_TYPES_IN_APPLY_CODE_ON_LISTING_API
                            ],
                    };
                    setHelperTextOnSuccess();
                    setFormData(
                        `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
                        couponCodeData
                    );
                    setValue(applied_coupon_form_key, couponCodeData);
                }
            })
            .catch((err: any) => {
                // Even if there is errorMessage from backend api, we'll only show
                // 'Invalid Coupon' as error message to the customer
                const isApiError: string = err?.response?.data?.message;
                if (isApiError) {
                    dispatch({
                        type: CHECKOUT_PAGES_ACTION_TYPES.SET_COUPON_CODE_HELPER_DATA,
                        payload: {
                            listingUuid,
                            data: {
                                helperTextDetails: {
                                    type: helper_text_types.error,
                                    text: "Invalid Coupon",
                                },
                            },
                        },
                    });
                    if (onError) {
                        onError();
                    }
                } else {
                    notify("Something went wrong! Please try again.", {
                        type: toast_type_keys.error,
                    });
                }
            })
            .finally(() => {
                setCouponCodeProcessing(false);
            });
    };

    return {
        onCouponCodeFieldChange,
        helperTextDetails,
        isCouponCodeApplied: appliedCouponCodeData.applied,
        appliedCouponType: appliedCouponCodeData.couponType,
        showProcessingState: processing,
        onRemoveCouponCodeClick,
        showRemoveCouponCodeState: !processing && appliedCouponCodeData.applied,
        showApplyCouponCodeState: !processing && !appliedCouponCodeData.applied,
        onApplyCouponCodeClick,
        setHelperTextOnSuccess,
        isMultipleUsersExist,
        currency,
    };
};
